/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Pagination from '../pagination';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../Utils/LoadingAnimations/TableLoadingSkeleton';
import LazyImage from './Components/imageComponent';
import { globalEnv } from '../../../config/baseConfig';
import { HeaderItem, sortingOrder } from './Components/common';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { DataNotFoundAnimation } from '../../Utils/ErrorAnimations/dataNotFoundAnim';

import meavanaLogo from '../../../assets/images/MeaVanaLogo.png';
import placeholder from '../../../assets/images/placeholder.png';
import { setSearchPlaceholderAction } from '../../../redux/actions/searchBarAction';

export default function TopicTable({
	reviewStatus
}: {
	reviewStatus?: string;
}) {
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const [selectedHeader, setSelectedHeader] = useState('createdAt');
	const [topicsCount, setTopicsCount] = useState(0);

	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(setSelectedSection('Topic Documents'));
		dispatch(setSearchPlaceholderAction('Search for Topics'));
	}, []);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { data, isError, isFetching, isLoading } = useQuery(
		[reviewStatus, page, selectedHeader, sortOrder, searchQuery],
		() => fetchTopics(page),
		{ keepPreviousData: true, staleTime: 5000, refetchOnWindowFocus: false }
	);

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery(['users', page + 1], () =>
				fetchTopics(page + 1)
			);
		}
	}, [data, page, queryClient]);

	useEffect(() => {
		if (data != null) {
			setTopicsCount(data.meta.total);
		}
	}, [data]);

	// Refresh the page after 20 minutes
	useEffect(() => {
		const intervalId = setInterval(() => {
			window.location.reload();
		}, 1000 * 60 * 20);
		return () => clearInterval(intervalId);
	}, []);

	async function fetchTopics(page: number) {
		const { data } = await axios.get(
			globalEnv.BASE_URL +
				`/topic/get-all?page=${page}&sortby=${selectedHeader}&order=${sortOrder}${
					searchQuery !== '' ? '&filter=' + searchQuery : ''
				}${reviewStatus != null ? '&status=' + reviewStatus : ''}`
		);
		return data;
	}

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Topic name"
					slugName="name"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Creation date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Subscribed"
					slugName="usersCount"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="on topic"
					slugName="onTopicUsers"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="active"
					slugName="activeUsers"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="images count"
					slugName="imagesCount"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<th className="whitespace-nowrap">
					<div className="font-semibold mr-2">Creator</div>
				</th>

				<th className="whitespace-nowrap">
					<div className="font-semibold mr-2">Status</div>
				</th>

				<th className="absolute p-2 whitespace-nowrap right-0">
					<div className="font-semibold text-left">
						{`Count: ${topicsCount}`}
					</div>
				</th>
			</tr>
		);
	};

	return (
		<section>
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-semibold uppercase dark:text-primary-text-dark text-primary-text-light ">
									<TableHeaders />
								</thead>
							)}
							<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
								{isLoading || isFetching || isError ? (
									<></>
								) : (
									data.topics.map((item: any) => (
										<TableData
											key={item._id}
											id={item._id}
											name={item.name}
											createdAt={item.createdAt}
											updatedAt={item.updatedAt}
											imagesCount={item.imagesCount}
											usersCount={item.usersCount}
											onTopicUsers={item.onTopicUsers}
											activeUsersCount={item.activeUsers}
											image={
												item.coverPic != null
													? item.coverPic
													: item.backgroundImage
											}
											creator={item.creator}
											status={item.reviewStatus}
											featuredStatus={item.featuredStatus}
										/>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>
		</section>
	);
}

type tableDateType = {
	id: string;
	name: string;
	image: any;
	createdAt: string;
	updatedAt: string;
	usersCount: number;
	onTopicUsers: number;
	activeUsersCount: number;
	imagesCount: number;
	creator: any;
	status: string;
	featuredStatus: string;
};

const TableData = ({
	id,
	name,
	image,
	createdAt,
	updatedAt,
	usersCount,
	onTopicUsers,
	activeUsersCount,
	imagesCount,
	creator,
	status,
	featuredStatus
}: tableDateType) => {
	useEffect(() => {
		console.log(featuredStatus);
	}, [featuredStatus]);

	const navigate = useNavigate();
	return (
		<tr
			className="cursor-pointer"
			onClick={() => {
				navigate(`../topic/${id}`);
			}}
		>
			<td className="col-span-1 p-2 break-all min-w-120">
				<div className="flex items-center">
					<div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage
							photo={
								image != null
									? image.url_low_res != null
										? image.url_low_res
										: placeholder
									: placeholder
							}
							style={
								'block object-cover object-center w-full aspect-[1/1] rounded-lg'
							}
						/>
					</div>
					<div className="font-medium text-gray-800 dark:text-white">
						{name}
					</div>
				</div>
			</td>

			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(createdAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>

			<td className="col-span-1 p-2 break-all w-20">
				<div className="text-center dark:text-gray-400">{usersCount}</div>
			</td>
			<td className="col-span-1 p-2 break-all w-20">
				<div className="text-center dark:text-gray-400">{onTopicUsers}</div>
			</td>
			<td className="col-span-1 p-2 break-all w-20">
				<div className="text-center dark:text-gray-400">{activeUsersCount}</div>
			</td>

			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{imagesCount}
				</div>
			</td>

			<td className="w-1/8 md:w-1/7 p-2 break-all">
				<div className="flex items-center">
					<div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage
							photo={
								creator.length > 0
									? creator[0].photo != null
										? creator.photo
										: `https://api.dicebear.com/7.x/bottts/svg?seed=${creator[0].username}`
									: meavanaLogo
							}
						/>
					</div>
					<div className="font-medium dark:text-primary-text-dark text-primary-text-light">
						{creator.length > 0 ? creator[0].username : 'Meavana'}
					</div>
				</div>
			</td>

			<td className="p-2 wsc px-6 whitespace-nowrap w-28">
				<div className="flex flex-row justify-center">
					<div
						className={
							'flex justify-center items-center py-1.5 px-1 rounded-full dark:text-primary-text-dark text-primary-text-light font-semibold transition-all duration-500 ' +
							(status === 'accepted'
								? 'bg-green-700 border-green-600'
								: status === 'featured'
								? 'bg-purple-700 border-purple-600'
								: status === 'pending' || status == null
								? 'bg-yellow-700 border-yellow-600'
								: status === 'additional review'
								? 'bg-yellow-500 border-yellow-400'
								: 'bg-red-700 border-red-600')
						}
					>
						<div className="text-xs text-primary-text-dark font-semibold mx-1">
							{status == null ? 'PENDING' : status.toUpperCase()}
						</div>
					</div>
					{featuredStatus != null &&
						(featuredStatus == 'none' || (
							<div
								className={
									'flex ml-2 justify-center items-center py-1.5 px-1 rounded-full dark:text-primary-text-dark text-primary-text-light font-semibold transition-all duration-500 bg-orange-600'
								}
							>
								<div className="text-xs text-primary-text-dark font-semibold mx-1">
									{featuredStatus.toUpperCase()}
								</div>
							</div>
						))}
				</div>
			</td>
		</tr>
	);
};
